import { onScopeDispose, toValue, watch, type MaybeRefOrGetter } from 'vue'

import { registerAndroidBackHandler, type AndroidBackHandler } from '@/services/androidBackService'

/**
 * Closes an overlay on the Android hardware back button.
 *
 * While `isActive` is truthy, `onBack` is registered with the global back
 * interceptor so the back gesture closes this overlay instead of navigating the
 * route. Registration is automatically released when the overlay closes or the
 * component scope is disposed.
 *
 * Use this for any dismissible transient UI on mobile — dialogs, bottom sheets,
 * mobile detail panels, the search palette — so back closes the top-most layer
 * first and only falls through to route history / app exit once nothing is open.
 *
 * ```ts
 * useAndroidBackHandler(isOpen, close)
 * ```
 */
export function useAndroidBackHandler(
  isActive: MaybeRefOrGetter<boolean>,
  onBack: AndroidBackHandler,
): void {
  let unregister: (() => void) | null = null

  const cleanup = () => {
    if (unregister) {
      unregister()
      unregister = null
    }
  }

  watch(
    () => toValue(isActive),
    (active) => {
      if (active) {
        if (!unregister) {
          unregister = registerAndroidBackHandler(onBack)
        }
      } else {
        cleanup()
      }
    },
    { immediate: true },
  )

  onScopeDispose(cleanup)
}
